/**
 * Sentry Configuration for Frontend
 * Production error tracking and performance monitoring
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for error tracking
 */
export function initSentry() {
  // Only initialize if DSN is provided
  if (!process.env.REACT_APP_SENTRY_DSN) {
    console.log('⚠️ REACT_APP_SENTRY_DSN not set - Sentry disabled');
    return;
  }

  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV || 'development',

    // Performance monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true, // Privacy: mask all text
        blockAllMedia: true, // Privacy: block all media
      }),
    ],

    // Sample rates
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0, // 10% in prod
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Release tracking
    release: process.env.REACT_APP_VERSION || 'development',

    // Filter out sensitive data
    beforeSend(event) {
      // Remove sensitive query params
      if (event.request && event.request.url) {
        try {
          const url = new URL(event.request.url);
          url.searchParams.delete('token');
          url.searchParams.delete('jwt');
          event.request.url = url.toString();
        } catch (e) {
          // Invalid URL, skip
        }
      }

      // Remove localStorage data (may contain tokens)
      if (event.contexts && event.contexts.localStorage) {
        delete event.contexts.localStorage;
      }

      return event;
    },

    // Ignore certain errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded', // React 18 dev warning
      'Non-Error promise rejection', // Unhandled promise rejections
      'Network request failed', // Expected network errors
      'Failed to fetch', // Expected fetch errors
      'Load failed', // Expected load errors
      'AbortError', // User-initiated aborts
    ],
  });

  console.log('✅ Sentry initialized (frontend):', {
    environment: process.env.NODE_ENV || 'development',
    tracing: 'enabled',
    replay: 'enabled',
  });
}

/**
 * Capture exception with context
 * @param {Error} error - Error object
 * @param {object} context - Additional context
 */
export function captureException(error, context = {}) {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.captureException(error, {
      tags: context.tags,
      extra: context.extra,
      level: context.level || 'error',
    });
  } else {
    console.error('Error (Sentry disabled):', error);
  }
}

/**
 * Capture message with context
 * @param {string} message - Message to capture
 * @param {string} level - Severity level
 * @param {object} context - Additional context
 */
export function captureMessage(message, level = 'info', context = {}) {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.captureMessage(message, {
      level,
      tags: context.tags,
      extra: context.extra,
    });
  } else {
    console.log(`Message (Sentry disabled) [${level}]:`, message);
  }
}

/**
 * Set user context
 * @param {object} user - User data
 */
export function setUser(user) {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.setUser(
      user
        ? {
            id: user.id,
            username: user.username,
            // Don't include email or other PII
          }
        : null
    );
  }
}

/**
 * Add breadcrumb for debugging
 * @param {object} breadcrumb - Breadcrumb data
 */
export function addBreadcrumb(breadcrumb) {
  if (process.env.REACT_APP_SENTRY_DSN) {
    Sentry.addBreadcrumb(breadcrumb);
  }
}

export { Sentry }; // Export for advanced usage
